import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { map, first } from "rxjs/operators";
import { MatLegacyDialog as MatDialog} from "@angular/material/legacy-dialog";

import { environment } from "../../environments/environment";
import { IUser } from "../_models";
import { StorageService } from "../_services/storage.service";
import { UserService } from "../_services/user.service";
import { AppGlobals } from "../app.globals";

import { Md5 } from "ts-md5/dist/md5"

@Injectable({ providedIn: "root" })
export class AuthenticationService {
    private currentUserSubject: BehaviorSubject<IUser>;
    public currentUser: Observable<IUser>;

    constructor(private http: HttpClient, private storageService: StorageService, private appGlobals: AppGlobals, private userService: UserService, private router: Router, public dialogRef: MatDialog) {
        const currentUser = this.storageService.getStorage(appGlobals.localStorageKeys.currentUser);

        this.currentUserSubject = new BehaviorSubject<IUser>(currentUser);
        this.currentUser = this.currentUserSubject.asObservable();
    }

    public get currentUserValue(): IUser {
        return this.currentUserSubject.value;
    }

    public set currentUserValue(value: IUser) {
        this.currentUserSubject.next(value);
    }

    login(username: string, password: string, isWindowsAuthentication: boolean) {
        const headers = new HttpHeaders();
        headers.append("Content-Type", "application/x-www-form-urlencoded");

        const options = { headers: headers };

        const hashedPassword = !isWindowsAuthentication ? Md5.hashStr(password) : "";

        const data = `grant_type=password&username=${username}&password=${hashedPassword}&client_id=E77BF3B2-E587-40D2-956B-6F18BF1EC04F`;

        return this.http.post<any>(`${environment.apiUrl}/token`, data, options)
            .pipe(map((result: any) => {
                let user: IUser = null;

                // login successful if there's a jwt token in the response
                if (result && result.access_token) {
                    user = this.getUserFromResult(result);

                    // store user details and jwt token in local storage to keep user logged in between page refreshes
                    this.storageService.store(this.appGlobals.localStorageKeys.currentUser, user);

                    const timer = this.storageService.getStorage(this.appGlobals.localStorageKeys.currentUser).expiresIn;
                    const d = new Date();
                    this.storageService.store(this.appGlobals.localStorageKeys.timer, d.setSeconds(d.getSeconds() + timer));

                    this.currentUserSubject.next(user);
                }

                return user;
            }));
    }

    logout(isSessionTimeout) {
        if (this.currentUserValue) {
            this.userService.logout(this.currentUserValue.studentLoginId, this.currentUserValue.id, isSessionTimeout)
                .pipe(first())
                .subscribe(() => {
                    this.dialogRef.closeAll();

                    this.storageService.clear(this.appGlobals.localStorageKeys.currentUser);
                    this.currentUserSubject.next(null);

                    this.router.navigate(["/login"]);
                });
        }
    }

    refreshAccessToken(): Observable<IUser> {
        const currentUser = this.storageService.getStorage(this.appGlobals.localStorageKeys.currentUser);
        const token = currentUser ? currentUser.refreshToken : null;

        if (token) {
            const headers = new HttpHeaders();
            headers.append("Content-Type", "application/x-www-form-urlencoded");

            const options = { headers: headers };

            const data = `grant_type=refresh_token&refresh_token=${token}&client_id=E77BF3B2-E587-40D2-956B-6F18BF1EC04F`;

            return this.http.post<any>(`${environment.apiUrl}/token`, data, options)
                .pipe(map((result: any) => {
                    let user: IUser = null;

                    // login successful if there's a jwt token in the response
                    if (result && result.access_token) {
                        user = this.getUserFromResult(result);

                        user.imageSrc = currentUser.imageSrc;
                        user.studentLoginId = currentUser.studentLoginId;
                        user.hasAdmissionsApplication = currentUser.hasAdmissionsApplication;
                        user.hasValidAdmissionsApplication = currentUser.hasValidAdmissionsApplication;
                        user.userNotifications = currentUser.userNotifications;
                        user.hasValidProgramCode = currentUser.hasValidProgramCode;
                        user.hasProgramCodeWithRequiredAdmissions = currentUser.hasProgramCodeWithRequiredAdmissions;
                        user.programCodes = currentUser.programCodes;
                        user.canApplyForHousing = currentUser.canApplyForHousing;
                        user.canApplyForAdmissions = currentUser.canApplyForAdmissions;
                        user.hasAdmissionsForProgram = currentUser.hasAdmissionsForProgram;
                        user.admissionsStartDate = currentUser.admissionsStartDate;
                        user.hasIdentityDocumentInfo = currentUser.hasIdentityDocumentInfo;
                        user.identityDocumentInfoExpired = currentUser.identityDocumentInfoExpired;
                        user.identityDocumentInformationNeedsStudentReview = currentUser.identityDocumentInformationNeedsStudentReview;
                        user.identityDocumentInformationNeedsReview = currentUser.identityDocumentInformationNeedsReview;
                        user.hasEmergencyContacts = currentUser.hasEmergencyContacts;
                        user.canViewOrientation = currentUser.canViewOrientation;
                        user.hasValidOrientation = currentUser.hasValidOrientation;
                        user.canModifyOrientation = currentUser.canModifyOrientation;
                        user.canViewImmigration = currentUser.canViewImmigration;

                        // store user details and jwt token in local storage to keep user logged in between page refreshes
                        this.storageService.store(this.appGlobals.localStorageKeys.currentUser, user);

                        const timer = this.storageService.getStorage(this.appGlobals.localStorageKeys.currentUser).expiresIn;
                        const d = new Date();
                        this.storageService.store(this.appGlobals.localStorageKeys.timer, d.setSeconds(d.getSeconds() + timer));

                        this.currentUserSubject.next(user);
                    }

                    return user;
                }));
        } else {
            return (throwError("No refresh token available")) as any;
        }
    }

    private getUserFromResult(result: any) {
        const user: IUser = {
            id: result.id,
            email: result.email,
            firstName: result.firstName,
            lastName: result.lastName,
            preferredName: result.preferredName,
            accessToken: result.access_token,
            refreshToken: result.refresh_token,
            gender: result.gender,
            isAuthenticated: true,
            expiresIn: result.expires_in,
            studentCode: result.studentCode ? result.studentCode : null,
            attachmentId: (result.attachmentId ? parseInt(result.attachmentId) : null) as any,
            requiresPasswordChange: result.requiresPasswordChange === "True",
            studentLoginId: result.studentLoginId,
            imageSrc: result.imageSrc,
            currencyId: (result.currencyId ? parseInt(result.currencyId) : null) as any,
            studentId: (result.studentId ? parseInt(result.studentId) : null) as any,
            providerId: (result.providerId ? parseInt(result.providerId) : null) as any,
            funnelId: (result.funnelId ? parseInt(result.funnelId) : null) as any,
            schoolId: (result.schoolId ? parseInt(result.schoolId) : null) as any,
            bbUniqueId: (result.bbUniqueId ? parseInt(result.bbUniqueId) : null) as any,
            hasAdmissionsApplication: false,
            hasValidAdmissionsApplication: false,
            userNotifications: [],
            programCodes: result.programCodes ? result.programCodes.split(",") : null,
            hasValidProgramCode: false,
            hasProgramCodeWithRequiredAdmissions: false,
            hasAdmissionsForProgram: false,
            canApplyForHousing: false,
            canApplyForAdmissions: false,
            admissionsStartDate: null,
            hasValidOrientation: false,
            canModifyOrientation: false,
            canViewOrientation: false,
            canViewImmigration: false,
            hasIdentityDocumentInfo: result.hasIdentityDocumentInformation === "True",
            identityDocumentInfoExpired: result.identityDocumentInfoExpired === "True",
            identityDocumentInformationNeedsStudentReview: result.identityDocumentInformationNeedsStudentReview === "True",
            identityDocumentInformationNeedsReview: false,
            hasEmergencyContacts: result.hasEmergencyContactInformation === "True",
            profileImageNeedsStudentReview: false
        };

        return user;
    }
}
